import type { District } from '../types/gameTypes';

// Perimeter layout: 28 tiles render as an 8x8 ring (8 top, 8 bottom, 6 left, 6 right,
// corners shared). Expansion tiles grow the ring automatically (e.g. 30 -> 8x9).
export interface GridCell {
  row: number; // 1-based, 1 = top row
  column: number; // 1-based, 1 = left column
  isCorner: boolean;
}

export interface PerimeterLayout {
  rows: number;
  columns: number;
  capacity: number; // number of perimeter cells of the chosen grid
  cells: GridCell[]; // indexed by tile index
}

const ringCells = (rows: number, columns: number): GridCell[] => {
  const cells: GridCell[] = [];

  // Bottom row, right -> left (tile 0 sits on the bottom-right "Payroll Plaza" corner)
  for (let column = columns; column >= 1; column -= 1) {
    cells.push({ row: rows, column, isCorner: column === 1 || column === columns });
  }
  // Left column, bottom -> top (bottom-left already placed)
  for (let row = rows - 1; row >= 1; row -= 1) {
    cells.push({ row, column: 1, isCorner: row === 1 || row === rows });
  }
  // Top row, left -> right (top-left already placed)
  for (let column = 2; column <= columns; column += 1) {
    cells.push({ row: 1, column, isCorner: column === columns });
  }
  // Right column, top -> bottom (both right corners already placed)
  for (let row = 2; row <= rows - 1; row += 1) {
    cells.push({ row, column: columns, isCorner: false });
  }

  return cells;
};

const chooseGrid = (count: number) => {
  let best: { rows: number; columns: number; capacity: number } | null = null;

  for (let rows = 3; rows <= 20; rows += 1) {
    for (let columns = rows; columns <= 20; columns += 1) {
      const capacity = 2 * (rows + columns) - 4;
      if (capacity < count) continue;

      if (!best) {
        best = { rows, columns, capacity };
        continue;
      }
      const slack = capacity - count;
      const bestSlack = best.capacity - count;
      const squareness = columns - rows;
      const bestSquareness = best.columns - best.rows;

      if (slack < bestSlack || (slack === bestSlack && squareness < bestSquareness)) {
        best = { rows, columns, capacity };
      }
    }
  }

  return best ?? { rows: 6, columns: Math.max(4, Math.ceil(count / 2)), capacity: count };
};

/**
 * Perimeter layout for the live board.
 *
 * When `board` is supplied the four CORNER LANDMARKS (Payroll Plaza, Audit & Tax
 * Bureau, Investment Bank, Securities Exchange) are PINNED to the four corner
 * cells of the ring, whatever the grid dimensions are. Every other tile fills the
 * remaining ring cells in board order, so annexations can never:
 *   - overwrite an existing tile's cell,
 *   - let two tiles share a cell (overlap), or
 *   - drift a landmark off its cardinal/corner position when the grid grows
 *     (28 tiles → 8×8 exact fit, 30 tiles → 8×9, 32 → 9×9 …).
 *
 * Without `board` (legacy count-only callers) the old proportional mapping applies.
 */
export const buildPerimeterLayout = (
  count: number,
  board?: { name: string }[],
): PerimeterLayout => {
  const safeCount = Math.max(1, count);
  const grid = chooseGrid(safeCount);
  const ring = ringCells(grid.rows, grid.columns);
  const cells: GridCell[] = [];

  // Corner ring indices in walk order: bottom-right, bottom-left, top-left, top-right.
  const cornerRingIndices = [0, grid.columns - 1, grid.columns + grid.rows - 2, 2 * grid.columns + grid.rows - 3];

  const landmarkIndices =
    board && board.length === safeCount
      ? board.map((tile, index) => ({ tile, index })).filter(({ tile }) => CORNER_TILES.has(tile.name)).map(({ index }) => index)
      : [];

  if (landmarkIndices.length === 4) {
    // ---- Pinned layout: landmarks own the corners, everyone else fills the rest ----
    const pinned: (GridCell | undefined)[] = new Array(safeCount);
    const takenRing = new Set<number>();
    landmarkIndices.forEach((tileIndex, cornerSlot) => {
      pinned[tileIndex] = ring[cornerRingIndices[cornerSlot]];
      takenRing.add(cornerRingIndices[cornerSlot]);
    });
    const freeRing = ring.filter((_, ringIndex) => !takenRing.has(ringIndex));
    const fillerIndices = Array.from({ length: safeCount }, (_, index) => index).filter(
      (index) => !landmarkIndices.includes(index),
    );
    fillerIndices.forEach((tileIndex, fillerSlot) => {
      // Proportional spread across the free ring cells — no overlaps, no gaps beyond slack.
      pinned[tileIndex] = freeRing[Math.min(freeRing.length - 1, Math.floor((fillerSlot * freeRing.length) / fillerIndices.length))];
    });
    return { rows: grid.rows, columns: grid.columns, capacity: grid.capacity, cells: pinned as GridCell[] };
  }

  for (let index = 0; index < safeCount; index += 1) {
    // Exact fit (28 tiles) maps 1:1, so corners land on tiles 0, 7, 14 and 21.
    const ringIndex =
      grid.capacity === safeCount ? index : Math.floor((index * grid.capacity) / safeCount);
    cells.push(ring[ringIndex]);
  }

  return { rows: grid.rows, columns: grid.columns, capacity: grid.capacity, cells };
};

export const DISTRICT_STYLES: Record<string, string> = {
  DISTRICT_A: 'border-sky-500/70 text-sky-100',
  DISTRICT_B: 'border-emerald-500/70 text-emerald-100',
  DISTRICT_C: 'border-violet-500/70 text-violet-100',
  DISTRICT_D: 'border-rose-500/70 text-rose-100',
  TRANSIT: 'border-cyan-400/70 text-cyan-100',
  FINANCIAL: 'border-amber-400/70 text-amber-100',
  SPECIAL: 'border-slate-500/70 text-slate-200',
};

export const DISTRICT_ACCENTS: Record<District, string> = {
  DISTRICT_A: 'bg-sky-400',
  DISTRICT_B: 'bg-emerald-400',
  DISTRICT_C: 'bg-violet-400',
  DISTRICT_D: 'bg-rose-400',
  DISTRICT_E: 'bg-emerald-300',
  DISTRICT_F: 'bg-purple-300',
  TRANSIT: 'bg-cyan-300',
  FINANCIAL: 'bg-amber-300',
  SPECIAL: 'bg-slate-400',
};

// Landmark corners of the ring: Payroll Plaza, Audit & Tax, Investment Bank, Securities Exchange.
export const CORNER_TILES = new Set(['Payroll Plaza', 'Audit & Tax Bureau', 'Investment Bank', 'Securities Exchange']);